import {
  Component,
  ChangeDetectionStrategy,
  DestroyRef,
  computed,
  inject,
  signal,
  effect,
  untracked,
  viewChild,
} from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { AppConfigStore } from '../../../state/app-config.repository';
import { AudioMuteStates, UserSettingsStore } from '../../../state/user-settings.repository';
import { MediaConfigStore } from '../../../state/media-config.repository';
import { DeviceService } from '../../../core/services/device.service';
import { camErrorMessage, LocalStreamService } from '../../../core/services/local-stream.service';
import { MediaPermissionsService } from '../../../core/services/media-permissions.service';
import { OdinConnectionStatus, OdinService } from '../../../core/services/odin.service';
import { BackgroundBlurService } from '../../../core/services/background-blur.service';
import { getPercentage } from '../../../shared/helpers/number';
import { FaDuotoneIconComponent, FaIconComponent } from '@fortawesome/angular-fontawesome';
import { TooltipDirective } from '../../../shared/components/ui/tooltip/tooltip.directive';
import {
  faMicrophone,
  faMicrophoneSlash,
  faVideo,
  faVideoSlash,
  faCameraWeb,
  faHeadset,
  faUser,
  faTriangleExclamation,
  faLock,
  faKey,
  faDoorClosed,
} from '@fortawesome/pro-duotone-svg-icons';
import { SelectField } from '../../../shared/components/form-fields/select-field/select-field';
import { AutocompleteField } from '../../../shared/components/form-fields/autocomplete-field/autocomplete-field';
import { Room, RoomsStore } from '../../../state/rooms.repository';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSlider, MatSliderThumb } from '@angular/material/slider';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatSliderWrapper } from '../../../shared/components/material-wrappers/mat-slider-wrapper/mat-slider-wrapper';
import { MatToggleWrapper } from '../../../shared/components/material-wrappers/mat-toggle-wrapper/mat-toggle-wrapper';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { VolumeIcon } from '../../../shared/components/volume-icon/volume-icon';
import { SplitToggleButton } from '../../../shared/components/ui/buttons/split-toggle-button/split-toggle-button';
import { ExpansionPanel } from '../../../shared/components/ui/expansion-panel/expansion-panel';
import { AppService } from '../../../core/services/app.service';
import { error, isFirefoxBrowser } from '@4players/odin-common';
import { E2eEncryptionPanel } from '../e2e-encryption-panel/e2e-encryption-panel';
import { ScheduleDisplay } from '../../../shared/components/ui/schedule-display/schedule-display';
import { JoinApprovalOverlay } from '../join-approval-overlay/join-approval-overlay';
import { createRoomState } from '../room-state';
import { PermissionStore } from '../../../state/permissions.repository';
import { RoomDialogService } from '../../rooms';
import { AppUpdateNotice } from '../app-update-notice/app-update-notice';
import { TrialNotice } from '../trial-notice/trial-notice';
import { TrialService } from '../../../core/services/trial.service';
import { NativeVideoPipe } from '../../../shared/pipes/native-video.pipe';
import { VideoMountDirective } from '../../../shared/directives/video-mount.directive';
import { isTauri } from '../../../shared/helpers/platform';

@Component({
  selector: 'andor-preparation-area',
  standalone: true,
  imports: [
    NativeVideoPipe,
    VideoMountDirective,
    AppUpdateNotice,
    TrialNotice,
    AsyncPipe,
    FormsModule,
    ReactiveFormsModule,
    FaDuotoneIconComponent,
    SelectField,
    MatSlider,
    MatSliderThumb,
    MatSlideToggle,
    MatProgressSpinner,
    MatSliderWrapper,
    MatToggleWrapper,
    TranslatePipe,
    VolumeIcon,
    SplitToggleButton,
    ExpansionPanel,
    E2eEncryptionPanel,
    FaIconComponent,
    TooltipDirective,
    AutocompleteField,
    ScheduleDisplay,
    JoinApprovalOverlay,
  ],
  templateUrl: './preparation-area.html',
  styleUrl: './preparation-area.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PreparationArea {
  micActive = toSignal(this.userSettingsStore.micActive$(), { initialValue: false });
  camStream = toSignal(this.localStream.camStream$);
  expandedPanel = signal<'audio' | 'video' | null>(null);
  permissionsDismissed = signal(false);
  highlightMicAccess = signal(false);
  highlightCamAccess = signal(false);
  connecting = computed(() => this._odinService.status() === OdinConnectionStatus.connecting);
  connectionError = this._odinService.connectionError;

  // User name with validation
  userNameControl = new FormControl('', [
    Validators.required,
    Validators.minLength(3),
    Validators.maxLength(50),
    Validators.pattern(/^[^\d]*$/),
  ]);

  protected readonly roomId = toSignal(this.userSettingsStore.roomId$, { initialValue: '' });

  readonly roomState = createRoomState(this.roomId);

  // --- EMPTY STATE --------------------------------------------------------
  // Gates the "Create a room" CTA in the empty state; creating opens the
  // dialog and retargets prep to the new room (see `createRoom`).
  readonly canManageRooms = toSignal(inject(PermissionStore).manageRooms$, { initialValue: false });
  private readonly _roomDialog = inject(RoomDialogService);
  /** An expired trial admits nobody (the server refuses too). */
  protected readonly trial = inject(TrialService);
  // Hidden rooms (instant / invite-link) are never browsable, so they don't
  // count as "public" rooms to fall back to.
  private readonly _hasPublicRooms = computed(() => this._rooms().some((r) => !r.isHidden));
  // Previously a dead-end when the invited room was gone. Now an invited user can
  // always OPEN their room on demand (old invite links resolve to a hidden
  // instant room), so a present `roomId` is never a dead-end — this only trips
  // in the (now effectively unreachable) no-target case.
  readonly noPublicRooms = computed(
    () => this.roomState() === 'unknown' && !this._hasPublicRooms() && !this.roomId().trim()
  );

  // The target room's schedule (if any) — drives the schedule display.
  protected readonly roomSchedule = computed(() => {
    const id = this.roomId();
    return id ? this._rooms().find((r) => r.roomId === id)?.schedule : undefined;
  });

  // Set while waiting for a moderator to admit this user (knock-to-join).
  protected readonly pendingApproval = this._odinService.pendingApproval;

  // --- Room picker (recovery) ---------------------------------------------
  // When the invited roomId doesn't exist, the connection-error state offers
  // the same room autocomplete as the room-login screen so the user can pick a
  // real room instead of being stuck. Local filtering mirrors room-login.
  private readonly _roomsStore = inject(RoomsStore);
  private readonly _rooms = toSignal(this._roomsStore.rooms$, { initialValue: [] as Room[] });
  protected readonly roomQuery = signal('');
  protected readonly filteredRooms = computed<Room[]>(() => {
    const q = this.roomQuery().trim().toLowerCase();
    // Hidden rooms (e.g. an invite-link instant room) are not offered in the
    // recovery picker — it exists to fall back to a real, listed room.
    const rooms = this._rooms().filter((r) => !r.isHidden);
    if (!q) return rooms;
    return rooms.filter((r) => r.name.toLowerCase().includes(q) || r.roomId.toLowerCase() === q);
  });

  // Invite links can point at a room that is not in the synced list — notably a
  // hidden/instant room, which is never broadcast. Resolve such a roomId by id
  // (once) so its name/schedule can be shown before joining. `lookupRoom`
  // upserts it into the store; browse lists still filter hidden rooms out.
  private readonly _lookedUpRoomIds = new Set<string>();
  protected readonly displayRoom = (room: Room): string => room.name;
  protected readonly roomKey = (room: Room): string => room.roomId;

  // The e2e component disappears on the error/connecting branches, so the
  // query must stay nullable.
  private readonly _encryption = viewChild(E2eEncryptionPanel);

  // Invited users always join an EXISTING room (by roomId from the invite
  // link). A missing room surfaces as a ROOM_NOT_FOUND connection error rather
  // than a create, so the label is always "Join Meeting".
  readonly joinButtonLabel = computed(() => 'Join Meeting');

  // Volume gate (dBFS)
  volumeGate = signal(-35);
  volumeGatePercent = signal(0);
  volumeGateMin = -90;
  volumeGateMax = -1;

  // Background blur
  backgroundBlurEnabled = signal(false);
  backgroundBlurAmount = signal(15);
  isBackgroundBlurSupported = BackgroundBlurService.isPlatformSupported();
  // Blur runs on MediaPipe in the browser; the native capture has no such stage.
  protected readonly blurAvailable = !isTauri;

  protected readonly faMicrophone = faMicrophone;
  protected readonly faMicrophoneSlash = faMicrophoneSlash;
  protected readonly faVideo = faVideo;
  protected readonly faVideoSlash = faVideoSlash;
  protected readonly faCameraWeb = faCameraWeb;
  protected readonly faHeadset = faHeadset;
  protected readonly faUser = faUser;
  protected readonly faTriangleExclamation = faTriangleExclamation;
  protected readonly faLock = faLock;
  protected readonly faKey = faKey;
  protected readonly faDoorClosed = faDoorClosed;

  constructor(
    public appConfigStore: AppConfigStore,
    public userSettingsStore: UserSettingsStore,
    public deviceService: DeviceService,
    public localStream: LocalStreamService,
    public permissions: MediaPermissionsService,
    private _mediaConfig: MediaConfigStore,
    private _odinService: OdinService,
    private _appService: AppService,
    private _snackBar: MatSnackBar,
    private _translate: TranslateService
  ) {
    this.permissions.setMediaApprovalGate();
    if (this.permissions.microphoneAccess() === 'granted') {
      this.permissions.approveMediaAccess();
    }

    // Sync username from store
    this.userSettingsStore.userName$.pipe(takeUntilDestroyed()).subscribe((userName) => {
      this.userNameControl.patchValue(userName);
    });

    // Initialize volume gate from store
    const storedVolumeGate = this.userSettingsStore.value.volumeGate;
    if (storedVolumeGate !== undefined) {
      this.volumeGate.set(storedVolumeGate);
    }

    // Subscribe to audio input power level (throttled to 100ms)
    inject(DestroyRef).onDestroy(this._odinService.holdLevelPolling(100));
    let lastPowerLevelUpdate = 0;
    this._odinService.audioInput$.pipe(takeUntilDestroyed()).subscribe((audioInput) => {
      if (audioInput) {
        audioInput.addEventListener('PowerLevel', (event) => {
          const now = performance.now();
          if (now - lastPowerLevelUpdate < 100) return;
          lastPowerLevelUpdate = now;
          this.volumeGatePercent.set(
            getPercentage(event.payload.media.powerLevel, this.volumeGateMin, this.volumeGateMax)
          );
        });
      } else {
        this.volumeGatePercent.set(0);
      }
    });

    // Hide volume indicator when mic is muted
    this.userSettingsStore.mutedState$.pipe(takeUntilDestroyed()).subscribe((state) => {
      if (state !== AudioMuteStates.none) {
        this.volumeGatePercent.set(0);
      }
    });

    // Initialize blur from media config store
    this.backgroundBlurEnabled.set(this._mediaConfig.value.backgroundBlurEnabled);
    this.backgroundBlurAmount.set(this._mediaConfig.value.backgroundBlurAmount);

    effect(() => {
      const status = permissions.microphoneAccess();
      if (status === 'granted') {
        deviceService.refreshAudioInputDevices().then();
        deviceService.refreshAudioOutputDevices().then();
      }
    });

    effect(async () => {
      const status = permissions.cameraAccess();
      if (status === 'granted') {
        if (isFirefoxBrowser()) {
          await permissions.requestUserMedia('video').catch(error);
        }
        deviceService.refreshVideoDevices().then();
      }
    });

    // Resolve an invite-link roomId that isn't in the synced list once the
    // snapshot has arrived: a hidden/instant room resolves by id (so its
    // metadata can be displayed), and an old link carrying a slugified room
    // NAME retargets to the matching registry room (the settings update
    // re-fires this effect with the real id, which is then known). Reads the
    // room list untracked + guards on a per-id set so the upserts/retargets
    // it triggers don't loop the effect.
    effect(() => {
      const id = this.roomId().trim();
      if (!id || !this._appService.firstSnapshotReceived()) return;
      if (this._lookedUpRoomIds.has(id)) return;
      const known = untracked(() => this._rooms().some((r) => r.roomId === id));
      if (known) return;
      this._lookedUpRoomIds.add(id);
      void this._appService.resolveInviteRoom(id);
    });
  }

  toggleMic() {
    if (this.permissions.microphoneAccess() !== 'granted') {
      this.highlightMicAccess.set(true);
      setTimeout(() => this.highlightMicAccess.set(false), 1500);
      return;
    }
    const isMicActive = this.userSettingsStore.value.mutedState === AudioMuteStates.none;
    if (isMicActive) {
      this.userSettingsStore.update({ mutedState: AudioMuteStates.mic });
    } else {
      this.userSettingsStore.update({ mutedState: AudioMuteStates.none });
    }
  }

  async toggleCam() {
    if (this.permissions.cameraAccess() !== 'granted') {
      this.highlightCamAccess.set(true);
      setTimeout(() => this.highlightCamAccess.set(false), 1500);
      return;
    }
    if (this.camStream()) {
      this.localStream.stopCamStream('preparation-area-v2');
    } else {
      await this.startCamWithFeedback();
    }
  }

  /** Starts the camera, surfacing failures in a snackbar. */
  private async startCamWithFeedback() {
    try {
      await this.localStream.startCamStream();
    } catch (e) {
      console.error(e);
      this._snackBar.open(
        this._translate.instant(camErrorMessage(this.localStream.camError())),
        undefined,
        { duration: 5000, horizontalPosition: 'center', verticalPosition: 'top' }
      );
    }
  }

  async requestMic(): Promise<boolean> {
    this.permissions.approveMediaAccess();
    const result = await this.permissions.requestMic();
    return result === 'granted';
  }

  async requestCam() {
    const result = await this.permissions.requestCam();
    if (result === 'granted') {
      await this.startCamWithFeedback();
    }
  }

  async requestBoth() {
    this.permissions.approveMediaAccess();
    const success = await this.requestMic();
    if (success) {
      this.requestCam().then();
    }
  }

  dismissPermissions() {
    this.permissionsDismissed.set(true);
    this.userSettingsStore.update({ mutedState: AudioMuteStates.mic });
    this.permissions.approveMediaAccess();
  }

  updateVolumeGate(value: number) {
    this.volumeGate.set(value);
    this.userSettingsStore.store.update((state) => ({
      ...state,
      volumeGate: value,
    }));
  }

  updateBackgroundBlurEnabled() {
    const newValue = !this.backgroundBlurEnabled();
    this.backgroundBlurEnabled.set(newValue);
    this._mediaConfig.update({ backgroundBlurEnabled: newValue });
  }

  updateBackgroundBlurAmount(value: number) {
    this.backgroundBlurAmount.set(value);
    this._mediaConfig.update({ backgroundBlurAmount: value });
  }

  formatDBFS(value: number): string {
    return `${value}`;
  }

  isJoinDisabled(): boolean {
    if (this.trial.expired()) return true;
    if (!this.userNameControl.valid) return true;
    if (this._encryption()?.joinBlocked()) return true;
    return false;
  }

  async join(enforceWebrtcTransport?: boolean) {
    const userName = this.userNameControl.value;
    if (!userName || this.userNameControl.invalid) return;

    let roomId = this.userSettingsStore.value.roomId;
    if (!roomId) return;

    // An id that matches no room may still be an old invite link carrying the
    // slugified room NAME — resolve it (by id, then by name) before treating
    // it as a brand-new instant room. Usually a no-op store hit: the
    // constructor effect already retargeted the settings when the snapshot
    // arrived; this covers a join clicked before that.
    const resolved = await this._appService.resolveInviteRoom(roomId);
    if (resolved) roomId = resolved.roomId;

    const encryption = this._encryption();
    if (encryption && !encryption.commitForJoin(roomId)) return;

    this.userSettingsStore.update({ userName });
    const hasCam = !!this.camStream();
    // Invited users open their room by id. If it isn't a known registry room (an
    // old invite link / instant room), create it on demand as a hidden, open,
    // auto-closing room; the server ignores the flag when the room already
    // exists (so a real private/closed room still behaves as before). The name
    // falls back to the id for a cold link with no human name.
    const roomName = resolved?.name ?? (this.userSettingsStore.value.roomName || roomId);
    const success = await this._odinService.connect(roomId, enforceWebrtcTransport, hasCam, {
      createIfMissing: true,
      roomName,
    });
    if (success) {
      this._appService.wasInvited = false;
    }
  }

  retry(alwaysEnforceWebrtcTransport?: boolean) {
    if (alwaysEnforceWebrtcTransport) {
      this.userSettingsStore.update({ useWebRtc: true });
    }
    this.join(true);
  }

  cancel() {
    this._odinService.disconnect().then();
    this._odinService.resetConnectionError();
  }

  protected onRoomQuery(query: string) {
    this.roomQuery.set(query);
  }

  // Recover from a not-found room: retarget to the picked room, clear the
  // error, and drop back into the normal prep flow (the e2e panel reappears for
  // password-protected rooms via `roomState`). The user then presses Join.
  protected pickRoom(room: Room) {
    this.userSettingsStore.update({ roomId: room.roomId, roomName: room.name });
    this._odinService.resetConnectionError();
    this.roomQuery.set('');
  }

  // Empty-state CTA (manageRooms holders only): create a room, then retarget prep
  // to it. The refresh surfaces the new room in the store so `roomState` flips
  // from `unknown` to `known-*`, dropping back into the normal prep flow where
  // the user presses Join.
  protected async createRoom() {
    if (!this.canManageRooms()) return;
    const created = await this._roomDialog.promptCreateRoom();
    if (!created) return;
    await this._appService.refreshRooms();
    this.userSettingsStore.update({ roomId: created.roomId, roomName: created.name });
    this._odinService.resetConnectionError();
  }
}
